import type { UseToastOptions } from '@chakra-ui/react';
import type { FirebaseError } from 'firebase/app';
import { type Auth, type User } from 'firebase/auth';
import {
  type DocumentData,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { gameStage, type Fixture } from '@/models/fixture';
import type { gameResult } from '@/models/gameUpdate';
import { type Country, countriesInfo, Country as CountryEnum } from '@/models/country';
import type { joinLeagueResponse, leagueCreationDetails } from '@/models/league';

export type Dict<TValue> = { [Key: string]: TValue };
export function toDict<TValue>(
  snapshot: QuerySnapshot,
  keySelector: (doc: QueryDocumentSnapshot<DocumentData>) => string = (x) => x.id,
) {
  return snapshot.docs.reduce(
    (prev, x) => {
      prev[keySelector(x)] = x.data() as TValue;
      return prev;
    },
    {} as Dict<TValue>,
  );
}

export function getServerTimestamp(): Timestamp {
  return Timestamp.now();
}

export function successToastOptions(title: string, description?: string): UseToastOptions {
  return {
    title: title,
    description: description,
    status: 'success',
    duration: 5000,
    isClosable: true,
  };
}

export function errorToastOptions(title: string, description?: string): UseToastOptions {
  return {
    title: title,
    description: description,
    duration: 5000,
    status: 'error',
    isClosable: true,
  };
}

export function firebaseErrorToastOptions(title: string, error: FirebaseError): UseToastOptions {
  return {
    title: title,
    description: error.message,
    duration: 5000,
    status: 'error',
    isClosable: true,
  };
}

export function joinLeagueToastOptions(response: joinLeagueResponse): UseToastOptions {
  return {
    title: response.alreadyMember ? 'League Already Joined' : 'League Joined',
    status: response.alreadyMember ? 'info' : 'success',
    isClosable: true,
    duration: 5000,
    description: response.alreadyMember
      ? `You were already participating in ${response.name}.`
      : `You have joined ${response.name}.`,
  };
}

export function createLeagueToastOptions(details: leagueCreationDetails): UseToastOptions {
  return successToastOptions('League Created', `The league ${details.name} has been created.`);
}

export function isValidEmail(email: string) {
  const regex =
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
  return regex.test(email);
}

export const MIN_PASSWORD_LENGTH = 8;

/** Short suffix for email-related toast descriptions. */
export const EMAIL_DELIVERY_TOAST_HINT =
  "If you don't see it within a few minutes, check your spam or junk folder.";

export function validatePassword(password: string, confirmPassword: string): { valid: boolean; error?: string } {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return { valid: false, error: 'Password must be at least 8 characters.' };
  }
  if (password !== confirmPassword) {
    return { valid: false, error: 'Passwords do not match.' };
  }
  return { valid: true };
}

export function getAuthErrorMessage(error: FirebaseError): string {
  switch (error.code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password.';
    case 'auth/email-already-in-use':
      return 'An account already exists with this email.';
    case 'auth/weak-password':
      return 'Password must be at least 8 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait and try again.';
    case 'auth/requires-recent-login':
      return 'Please sign in again to continue.';
    case 'auth/account-exists-with-different-credential':
      return 'This email is linked to Google. Sign in with Google.';
    case 'auth/invalid-action-code':
      return 'This link is invalid or has already been used.';
    case 'auth/expired-action-code':
      return 'This link has expired. Please request a new one.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/credential-already-in-use':
      return 'This credential is already linked to another account.';
    case 'auth/provider-already-linked':
      return 'This sign-in method is already linked.';
    default:
      return error.message;
  }
}

export function hasPasswordProvider(user: User): boolean {
  return user.providerData.some((p) => p.providerId === 'password');
}

export function hasGoogleProvider(user: User): boolean {
  return user.providerData.some((p) => p.providerId === 'google.com');
}

/** Read emailVerified from the live auth session (ctx.user can lag after reload). */
export function isAuthUserEmailVerified(auth: Auth): boolean {
  return auth.currentUser?.emailVerified ?? false;
}

/** Reload auth user until emailVerified is true (after applyActionCode). */
export async function waitForEmailVerified(auth: Auth, maxAttempts = 25, delayMs = 200): Promise<boolean> {
  for (let i = 0; i < maxAttempts; i++) {
    await auth.authStateReady();
    const user = auth.currentUser;
    if (user?.emailVerified) {
      return true;
    }
    if (user) {
      await user.reload();
      if (user.emailVerified) {
        return true;
      }
    }
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return auth.currentUser?.emailVerified ?? false;
}

export function parseNumberInput(x: string) {
  if (x === '') {
    return 0;
  }
  return Math.abs(Number.parseInt(x));
}

export function ordinalSuffix(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export function convertStageToString(stage: gameStage): string {
  switch (stage) {
    case gameStage.groupStage:
      return 'Group Stage';
    case gameStage.roundOf32:
      return 'Round of 32';
    case gameStage.roundOf16:
      return 'Round of 16';
    case gameStage.quarterFinal:
      return 'Quarter Finals';
    case gameStage.semiFinal:
      return 'Semi Finals';
    case gameStage.thirdPlayoff:
      return 'Third Place Playoff';
    case gameStage.final:
      return 'Final';
    default:
      return 'Unknown';
  }
}

/** Kick-off in Europe/London, e.g. `6pm, Saturday 14th June`. */
export function formatFixtureKickoffLondon(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).formatToParts(date);

  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  const hour = Number.parseInt(map.hour ?? '0', 10);
  const minute = Number.parseInt(map.minute ?? '0', 10);
  const day = Number.parseInt(map.day ?? '0', 10);
  const weekday = map.weekday ?? '';
  const month = map.month ?? '';
  let dayPeriod = (map.dayPeriod ?? '').toLowerCase();
  if (dayPeriod === 'noon') dayPeriod = 'pm';
  if (dayPeriod === 'midnight') dayPeriod = 'am';

  const timeStr =
    minute === 0 ? `${hour}${dayPeriod}` : `${hour}:${String(minute).padStart(2, '0')}${dayPeriod}`;

  return `${timeStr}, ${weekday} ${day}${ordinalSuffix(day)} ${month}`;
}

/** Short date in Europe/London, e.g. `11/06`. */
export function formatFixtureDateShortLondon(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: '2-digit',
    month: '2-digit',
  }).format(date);
}

export function fixtureVenueLine(fixture: Fixture): string {
  if (fixture.venueName && fixture.cityName) {
    return `${fixture.venueName} · ${fixture.cityName}`;
  }
  return fixture.location;
}

/** City only for UI (no stadium). Uses `cityName` when set, else last segment of `location` after commas. */
export function fixtureCityDisplay(fixture: Pick<Fixture, 'cityName' | 'location'>): string {
  const c = fixture.cityName?.trim();
  if (c) return c;
  const loc = fixture.location?.trim() ?? '';
  if (!loc) return 'TBC';
  const parts = loc.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return parts[parts.length - 1]!;
  }
  return loc;
}

/**
 * User-facing fixture text: underscores → spaces; match-feeder slots as
 * "R32 1 Winner" / "SF 1 Loser" (stored labels stay "Winner R32_1", etc.).
 */
export function formatFixtureDisplayLabel(label: string | undefined | null): string {
  if (!label) return '';
  const trimmed = label.trim();
  const matchWinner = /^Winner (R32_\d+|R16_\d+|QF_\d+|SF_\d+|FINAL)$/.exec(trimmed);
  if (matchWinner) {
    return `${matchWinner[1]!.replace(/_/g, ' ')} Winner`;
  }
  const matchLoser = /^Loser (SF_\d+)$/.exec(trimmed);
  if (matchLoser) {
    return `${matchLoser[1]!.replace(/_/g, ' ')} Loser`;
  }
  return trimmed.replace(/_/g, ' ');
}

export function sortFixturesChronological(a: Fixture, b: Fixture): number {
  const d = a.date.toMillis() - b.date.toMillis();
  if (d !== 0) return d;
  return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
}

/** Newest kickoff first (inverse of chronological). */
export function sortFixturesRecentFirst(a: Fixture, b: Fixture): number {
  return sortFixturesChronological(b, a);
}

export function getPlayedFixturePairKeys(gameResults: gameResult[]): Set<string> {
  const keys = new Set<string>();
  for (const r of gameResults) {
    keys.add(`${r.countryA}:${r.countryB}`);
    keys.add(`${r.countryB}:${r.countryA}`);
  }
  return keys;
}

/** Upcoming fixtures only: no recorded result, kickoff still in the future. Earliest first. */
export function getUpcomingFixtures(fixtures: Fixture[], gameResults: gameResult[]): Fixture[] {
  const playedPairs = getPlayedFixturePairKeys(gameResults);
  const now = Date.now();
  return fixtures
    .filter((f) => {
      const pairKey = `${f.homeTeam}:${f.awayTeam}`;
      const reverseKey = `${f.awayTeam}:${f.homeTeam}`;
      if (playedPairs.has(pairKey) || playedPairs.has(reverseKey)) return false;
      return f.date.toMillis() > now;
    })
    .sort(sortFixturesChronological);
}

export function sortGameResultsRecentFirst(gameResults: gameResult[]): gameResult[] {
  return [...gameResults].sort((a, b) => {
    const ta = a.kickOffTime?.toMillis() ?? a.timestamp?.toMillis() ?? 0;
    const tb = b.kickOffTime?.toMillis() ?? b.timestamp?.toMillis() ?? 0;
    return tb - ta;
  });
}

export function fixtureSideDisplayName(fixture: Fixture, side: 'home' | 'away'): string {
  const team: Country = side === 'home' ? fixture.homeTeam : fixture.awayTeam;
  const slot = side === 'home' ? fixture.homeSlotLabel : fixture.awaySlotLabel;
  if (team !== CountryEnum.TBD) {
    return countriesInfo[team].name;
  }
  return slot ? formatFixtureDisplayLabel(slot) : countriesInfo[CountryEnum.TBD].name;
}

export {
  buildVenueNextFixtureIndex,
  fixtureMatchesVenue,
  getNextFixtureAtVenue,
  normalizeVenueName,
} from './fixtures/mapVenueFixtures';
